import { Injectable } from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { NavigationItem } from './navigation-item.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from '../../shared/services/auth.service';
import { EmployeeService } from '../../shared/services/employee.service';
import { Helper } from '../../shared/constants/helper';
import { PermissionConstant } from '../../shared/constants/permission-constant';
import { PermissionService } from '../../shared/services/permission.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  user: any;
  userData: any;
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(
    private readonly layoutService: VexLayoutService,
    private authService: AuthService,
    private employeeService: EmployeeService
  ) {
    const self: any = localStorage.getItem('self');
    if (self) {
      this.getSelfDetails();
    } else {
      this.loadNavigation();
    }
  }

  loadNavigation(): void {
    this._items.next([
      {
        type: 'subheading',
        label: 'Dashboard',
        availableTo: [PermissionConstant.ALL],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Home',
            route: '/home',
            icon: 'mat:dashboard',
            routerLinkActiveOptions: { exact: true },
            availableTo: [PermissionConstant.ALL],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Provisional Admission',
        availableTo: [PermissionConstant.BDE_ADD, PermissionConstant.BDE_LIST],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Add',
            route: '/bde/add',
            icon: 'mat:add_circle',
            availableTo: [PermissionConstant.BDE_ADD],
            is_hidden: this.user?.user_type === 'SUPER_ADMIN'
          },
          {
            type: 'link',
            label: 'List',
            route: '/bde/list',
            icon: 'mat:view_list',
            availableTo: [PermissionConstant.BDE_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'All',
            route: '/bde/all-student-list',
            icon: 'mat:diversity_3',
            availableTo: [PermissionConstant.BDE_ALL_LIST],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'General',
        availableTo: [PermissionConstant.GENERAL],
        is_hidden: false,
        children: [
          {
            type: 'dropdown',
            label: 'Admins',
            icon: 'mat:security',
            availableTo: [],
            is_hidden: this.user?.user_type !== 'SUPER_ADMIN',
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/admin/add',
                availableTo: [],
                is_hidden: this.user?.user_type !== 'SUPER_ADMIN'
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/admin/list',
                availableTo: [],
                is_hidden: this.user?.user_type !== 'SUPER_ADMIN'
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Centers',
            icon: 'mat:supervisor_account',
            availableTo: [PermissionConstant.CENTER_ADD, PermissionConstant.CENTER_LIST],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/center/add',
                availableTo: [PermissionConstant.CENTER_ADD],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/center/list',
                availableTo: [PermissionConstant.CENTER_LIST],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Sessions',
            icon: 'mat:access_time',
            availableTo: [PermissionConstant.SESSION],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/session/add',
                availableTo: [PermissionConstant.SESSION_ADD],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/session/list',
                availableTo: [PermissionConstant.SESSION_LIST],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Meetings',
            icon: 'mat:event',
            availableTo: [PermissionConstant.MEETINGS],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Persons',
                route: '/general/meetings/persons',
                availableTo: [],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Schedule',
                route: '/general/meetings/schedule',
                availableTo: [PermissionConstant.MEETING_SCHEDULE],
                is_hidden: false
              }
            ]
          },
          {
            type: 'link',
            label: 'Report',
            route: '/general/reports',
            icon: 'mat:dashboard_customize',
            routerLinkActiveOptions: { exact: true },
            availableTo: [PermissionConstant.GENERAL_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Employees',
        availableTo: [PermissionConstant.EMPLOYEE],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Departments',
            route: '/employees/department',
            icon: 'mat:domain',
            availableTo: [PermissionConstant.DEPARTMENT],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Designations',
            route: '/employees/designation',
            icon: 'mat:person_pin',
            availableTo: [PermissionConstant.DESIGNATION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add Employee',
            route: '/employees/employee/add',
            icon: 'mat:person_add_alt',
            availableTo: [PermissionConstant.EMPLOYEE_ADD],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List Employee',
            route: '/employees/employee/list',
            icon: 'mat:groups',
            availableTo: [PermissionConstant.EMPLOYEE_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Mark Attendance',
            route: '/employees/employee/mark-attendance',
            icon: 'mat:fingerprint',
            availableTo: [PermissionConstant.EMPLOYEE_ATTENDANCE],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Attendance',
            route: '/employees/employee/view-attendance',
            icon: 'mat:access_time',
            availableTo: [PermissionConstant.ALL],
            is_hidden: this.user && this.user.user_type !== 'EMPLOYEE'
          },
          {
            type: 'link',
            label: 'Leave',
            route: '/employees/employee/leave',
            icon: 'mat:event',
            availableTo: [PermissionConstant.ALL],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Resignation',
            route: '/employees/employee/resignation',
            icon: 'mat:person_off',
            availableTo: [PermissionConstant.RESIGNATION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Payroll',
            route: '/employees/employee/payroll',
            icon: 'mat:monetization_on',
            availableTo: [PermissionConstant.EMPLOYEE_PAYROLL],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Permissions',
            route: '/employees/permission',
            icon: 'mat:key',
            availableTo: [PermissionConstant.PERMISSION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Report',
            route: '/employees/reports',
            icon: 'mat:dashboard_customize',
            routerLinkActiveOptions: { exact: true },
            availableTo: [PermissionConstant.EMPLOYEE_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Modules',
        availableTo: [PermissionConstant.MODULE],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Schools',
            route: '/modules/school',
            icon: 'mat:school',
            availableTo: [PermissionConstant.SCHOOL],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Programs',
            route: '/modules/program',
            icon: 'mat:cast_for_education',
            availableTo: [PermissionConstant.PROGRAM],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Courses',
            route: '/modules/course',
            icon: 'mat:how_to_vote',
            availableTo: [PermissionConstant.COURSE],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Branches',
            route: '/modules/branches',
            icon: 'mat:spa',
            availableTo: [PermissionConstant.BRANCH],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Mappings',
            route: '/modules/mapping',
            icon: 'mat:pin_invoke',
            availableTo: [PermissionConstant.MAPPING],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Subjects',
            route: '/modules/subject',
            icon: 'mat:book',
            availableTo: [PermissionConstant.SUBJECT],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/modules/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.MODULE_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Classes',
        availableTo: [ PermissionConstant.PREVIOUS_CLASSES, PermissionConstant.UPCOMING_CLASSES, PermissionConstant.ASSIGNMENTS ],
        is_hidden: this.user?.user_type !== 'EMPLOYEE' && this.user?.employee_status !== 'TEACHING',
        children: [
          {
            type: 'link',
            label: 'Previous Classes',
            route: '/classes/previous-classes',
            icon: 'mat:school',
            availableTo: [PermissionConstant.PREVIOUS_CLASSES],
            is_hidden: this.user?.user_type !== 'EMPLOYEE' && this.user?.employee_status !== 'TEACHING'
          },
          {
            type: 'link',
            label: 'Upcoming Classes',
            route: '/classes/upcoming-classes',
            icon: 'mat:cast_for_education',
            availableTo: [PermissionConstant.UPCOMING_CLASSES],
            is_hidden: this.user?.user_type !== 'EMPLOYEE' && this.user?.employee_status !== 'TEACHING'
          },
          {
            type: 'link',
            label: 'Assignments',
            route: '/classes/assignments',
            icon: 'mat:assignment',
            availableTo: [PermissionConstant.ASSIGNMENTS],
            is_hidden: this.user?.user_type !== 'EMPLOYEE' && this.user?.employee_status !== 'TEACHING'
          },
          {
            type: 'link',
            label: 'Report',
            route: '/classes/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.REPORT_CLASSES],
            is_hidden: this.user?.user_type !== 'EMPLOYEE' && this.user?.employee_status !== 'TEACHING'
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Assessments',
        availableTo: [PermissionConstant.ASSIGNMENT_LIST],
        is_hidden:
          this.user &&
          this.user.user_type !== 'EMPLOYEE' &&
          this.user.employee_status !== 'TEACHING',
        children: [
          {
            type: 'link',
            label: 'Assignments',
            route: '/assessments/assignments',
            icon: 'mat:assignment',
            availableTo: [PermissionConstant.ASSIGNMENT_LIST],
            is_hidden:
              this.user &&
              this.user.user_type !== 'EMPLOYEE' &&
              this.user.employee_status !== 'TEACHING'
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Inventories',
        availableTo: [PermissionConstant.INVENTORY],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Store',
            route: '/inventory/store',
            icon: 'mat:store',
            availableTo: [PermissionConstant.INVENTORY_STORE],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Categories',
            route: '/inventory/category',
            icon: 'mat:category',
            availableTo: [PermissionConstant.INVENTORY_CATEGORY],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Department',
            route: '/inventory/department',
            icon: 'mat:groups',
            availableTo: [PermissionConstant.INVENTORY_DEPARTMENT],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Request',
            route: '/inventory/request',
            icon: 'mat:inventory_2',
            availableTo: [PermissionConstant.ALL],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Demand',
            route: '/inventory/demand',
            icon: 'mat:assignment',
            availableTo: [PermissionConstant.INVENTORY_DEMAND],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Allocation',
            route: '/inventory/allocation',
            icon: 'mat:assignment_turned_in',
            availableTo: [PermissionConstant.INVENTORY_ALLOCATION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/inventory/list',
            icon: 'mat:inventory',
            availableTo: [PermissionConstant.INVENTORY_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Transactions',
            route: '/inventory/transaction',
            icon: 'mat:receipt',
            availableTo: [PermissionConstant.INVENTORY_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/inventory/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.INVENTORY_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Books',
        availableTo: [PermissionConstant.BOOK],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Search',
            route: '/book/search',
            icon: 'mat:search',
            availableTo: [PermissionConstant.BOOK_SEARCH],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Categories',
            route: '/book/category',
            icon: 'mat:category',
            availableTo: [PermissionConstant.BOOK_CATEGORY],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/book/list',
            icon: 'mat:inventory',
            availableTo: [PermissionConstant.BOOK_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Suppliers',
            route: '/book/suppliers',
            icon: 'mat:transfer_within_a_station',
            availableTo: [PermissionConstant.BOOKS_SUPPLIERS],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Transactions',
            route: '/book/transaction',
            icon: 'mat:receipt',
            availableTo: [PermissionConstant.BOOK_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Settings',
            route: '/book/late-fee',
            icon: 'mat:build',
            availableTo: [PermissionConstant.BOOKS_SETTINGS],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/book/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.BOOK_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Libraries',
        availableTo: [PermissionConstant.LIBRARY],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'List',
            route: '/library/list',
            icon: 'mat:local_library',
            availableTo: [PermissionConstant.LIBRARIES_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Borrowings',
            route: '/library/borrowing',
            icon: 'mat:library_books',
            availableTo: [PermissionConstant.LIBRARY_BORROWING],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Transactions',
            route: '/library/transaction',
            icon: 'mat:receipt',
            availableTo: [PermissionConstant.LIBRARY_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/library/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.LIBRARY_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Leads',
        availableTo: [PermissionConstant.LEADS],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Website Leads',
            route: '/leads/manual',
            icon: 'mat:transfer_within_a_station',
            availableTo: [PermissionConstant.LEADS_WEBSITE_LEAD],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Students',
        availableTo: [PermissionConstant.STUDENT],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Search',
            route: '/student/search',
            icon: 'mat:search',
            availableTo: [PermissionConstant.STUDENT_SEARCH],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add',
            route: '/student/add',
            icon: 'mat:person_add',
            availableTo: [PermissionConstant.STUDENT_ADD],
            is_hidden: !(this.user && this.user.user_type == 'EMPLOYEE')
          },
          {
            type: 'link',
            label: 'Direct Students',
            route: '/student/direct-student',
            icon: 'mat:supervised_user_circle',
            availableTo: [PermissionConstant.STUDENT_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Verify Direct Students',
            route: '/student/verify-direct-student',
            icon: 'mat:how_to_reg',
            availableTo: [PermissionConstant.STUDENT_VERIFY],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Center Students',
            route: '/student/center-student',
            icon: 'mat:people_alt',
            availableTo: [PermissionConstant.STUDENT_CENTER],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Verify Center Students',
            route: '/student/verify-center-student',
            icon: 'mat:how_to_reg',
            availableTo: [PermissionConstant.STUDENT_VERIFY_CENTER],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Student Transactions',
            route: '/student/transactions',
            icon: 'mat:payments',
            availableTo: [PermissionConstant.STUDENT_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Verify Scholarships',
            route: '/student/verify-scholarship',
            icon: 'mat:check_circle',
            availableTo: [PermissionConstant.STUDENT_SCHOLARSHIP],
            is_hidden: !(this.user && this.user.user_type == 'SUPER_ADMIN')
          },
          {
            type: 'link',
            label: 'Scholarship Student',
            route: '/student/scholarship-student',
            icon: 'mat:person_search',
            availableTo: [PermissionConstant.STUDENT_SCHOLARSHIP_PAYMENT],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Attendance',
            route: '/student/attendance',
            icon: 'mat:fingerprint',
            availableTo: [PermissionConstant.STUDENT_ATTENDANCE],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Enrollment',
            route: '/student/enrollment',
            icon: 'mat:app_registration',
            availableTo: [PermissionConstant.STUDENT_ENROLLMENT],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Final Verification',
            route: '/student/finalised-student-list',
            icon: 'mat:done_all',
            availableTo: [PermissionConstant.FINALISED_STUDENT_LIST],
            is_hidden: !(this.user && this.user.user_type == 'SUPER_ADMIN')
          },
          {
            type: 'link',
            label: 'Progress Manager',
            route: '/student/progress-manager',
            icon: 'mat:assignment',
            availableTo: [PermissionConstant.STUDENT_ENROLLMENT],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Hostel Students',
            route: '/student/hostel-students',
            icon: 'mat:home',
            availableTo: [PermissionConstant.HOSTEL_STUDENT_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Transportation Students',
            route: '/student/transportation-students',
            icon: 'mat:directions_car',
            availableTo: [PermissionConstant.TRANSPORTATION_STUDENT_LIST],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Leave',
            route: '/student/leave',
            icon: 'mat:event',
            availableTo: [PermissionConstant.STUDENT_LEAVE],
            is_hidden: (this.user?.user_type !== 'EMPLOYEE')
          },
          {
            type: 'link',
            label: 'Subject',
            route: '/student/subject',
            icon: 'mat:library_books',
            availableTo: [PermissionConstant.STUDENT_SUBJECT],
            is_hidden: (this.user?.user_type !== 'EMPLOYEE')
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/student/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.STUDENT_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Accounts',
        availableTo: [PermissionConstant.ACCOUNT_ACCOUNT],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Receipt',
            route: '/account/receipts',
            icon: 'mat:credit_card',
            availableTo: [PermissionConstant.ACCOUNT_RECEIPT],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Receipt Transaction',
            route: '/account/receipt-transactions',
            icon: 'mat:monetization_on',
            availableTo: [PermissionConstant.ACCOUNT_RECEIPT_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Wallet Transaction',
            route: '/account/wallet-transactions',
            icon: 'mat:account_balance_wallet',
            availableTo: [PermissionConstant.ACCOUNT_WALLET_TRANSACTION],
            is_hidden: false
          },
         /* {
            type: 'link',
            label: 'Center Liability',
            route: '/account/center-liability',
            icon: 'mat:money_off',
            availableTo: [PermissionConstant.ACCOUNT_WALLET_TRANSACTION],
            is_hidden: false
          },*/
          {
            type: 'link',
            label: 'Student Fee',
            route: '/account/student-fee',
            icon: 'mat:point_of_sale',
            availableTo: [PermissionConstant.ACCOUNT_FINAL_STUDENT_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Student Other Fee',
            route: '/account/student-other-fee',
            icon: 'mat:inventory_2',
            availableTo: [PermissionConstant.ACCOUNT_FINAL_STUDENT_TRANSACTION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Student Transaction',
            route: '/account/student-transaction',
            icon: 'mat:receipt_long',
            availableTo: [PermissionConstant.ACCOUNT_STUDENT_TRANSACTION],
            is_hidden: false
          },
          /*{
            type: 'link',
            label: 'Fee Calculator',
            route: '/account/fee-calculator',
            icon: 'mat:calculate',
            availableTo: [PermissionConstant.ACCOUNT_FEE_CALCULATOR],
            is_hidden: false
          },*/
          {
            type: 'link',
            label: 'Reports',
            route: '/account/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.ACCOUNT_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Exams',
        availableTo: [PermissionConstant.EXAMS],
        is_hidden: false,
        children: [
          {
            type: 'dropdown',
            label: 'Mid',
            icon: 'mat:quiz',
            availableTo: [PermissionConstant.INTERNAL],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Sessions',
                route: '/exams/internal/session',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.INTERNAL_EXAM_SESSION],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Questions',
                route: '/exams/internal/questions',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.INTERNAL_EXAM_QUESTIONS],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Scheduler',
                route: '/exams/internal/scheduler',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.INTERNAL_EXAM_SCHEDULER],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Halls',
                route: '/exams/internal/halls',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.INTERNAL_EXAM_HALLS],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Evaluator',
                route: '/exams/internal/evaluator',
                icon: 'mat:analytics',
                availableTo: [PermissionConstant.INTERNAL_EXAM_EVALUATOR],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Result',
                route: '/exams/internal/result',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.INTERNAL_EXAM_RESULT],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Report',
                route: '/exams/internal/report',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.ALL],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Final',
            icon: 'mat:timer',
            availableTo: [PermissionConstant.FINAL],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Sessions',
                route: '/exams/final/session',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.FINAL_EXAM_SESSION],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Questions',
                route: '/exams/final/questions',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.FINAL_EXAM_QUESTIONS],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Scheduler',
                route: '/exams/final/scheduler',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.FINAL_EXAM_SCHEDULER],
                is_hidden: false
              },
              // {
              //   type: 'link',
              //   label: 'Eligible Students',
              //   route: '/exams/final/eligible-students',
              //   icon: 'mat:dashboard_customize',
              //   availableTo: [PermissionConstant.FINAL_EXAM_ELIGIBLE_STUDENTS],
              //   is_hidden: false
              // },
              {
                type: 'link',
                label: 'Halls',
                route: '/exams/final/halls',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.FINAL_EXAM_HALLS],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Evaluator',
                route: '/exams/final/evaluator',
                icon: 'mat:analytics',
                availableTo: [PermissionConstant.FINAL_EXAM_EVALUATOR],
                is_hidden: false
              },
             /* {
                type: 'link',
                label: 'Result',
                route: '/exams/final/result',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.FINAL_EXAM_RESULT],
                is_hidden: false
              },*/
              {
                type: 'link',
                label: 'Result',
                route: '/exams/final/student-result',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.FINAL_EXAM_RESULT],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Report',
                route: '/exams/final/report',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.ALL],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Entrance',
            icon: 'mat:description',
            availableTo: [PermissionConstant.ENTRANCE_EXAM],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Student',
                route: '/exams/entrance/student',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.ENTRANCE_EXAM_STUDENT],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Questions',
                route: '/exams/entrance/upload-question',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.ENTRANCE_EXAM_QUESTION],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Result',
                route: '/exams/entrance/result',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.ENTRANCE_EXAM_RESULT],
                is_hidden: false
              }
            ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'DOCS',
        availableTo: [PermissionConstant.DOCS],
        is_hidden: false,
        children: [
          {
            type: 'dropdown',
            label: 'Document',
            icon: 'mat:description',
            availableTo: [PermissionConstant.DOCS_DOCUMENT],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Bona Fide',
                route: '/docs/document/bona-fide',
                icon: 'mat:description',
                availableTo: [PermissionConstant.DOCS_BONA_FIDE],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Migration Certificate',
                route: '/docs/document/migration-certificate',
                icon: 'mat:description',
                availableTo: [PermissionConstant.DOCS_MIGRATION_CERTIFICATE],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Provisional Certificate',
                route: '/docs/document/provisional-certificate',
                icon: 'mat:description',
                availableTo: [PermissionConstant.DOCS_PROVISIONAL_CERTIFICATE],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Character Certificate',
                route: '/docs/document/character-certificate',
                icon: 'mat:description',
                availableTo: [PermissionConstant.DOCS_CHARACTER_CERTIFICATE],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Duplicate DMC',
                route: '/docs/document/duplicate-dmc',
                icon: 'mat:description',
                availableTo: [PermissionConstant.DOCS_DUPLICATE_DMC],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Degree',
                route: '/docs/document/degree',
                icon: 'mat:description',
                availableTo: [PermissionConstant.DOCS_DEGREE],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Marksheet',
                route: '/docs/document/marksheet',
                icon: 'mat:description',
                availableTo: [PermissionConstant.DOCS_MARKSHEET],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Doc Req',
                route: '/docs/document/req-from-web',
                icon: 'mat:description',
                availableTo: [PermissionConstant.DOCS_MARKSHEET],
                is_hidden: false
              },
            ]
          },
          {
            type: 'dropdown',
            label: 'No Dues Slip',
            icon: 'mat:file_copy',
            availableTo: [PermissionConstant.DOCS_NO_DUE_SLIP_ACADEMIC,PermissionConstant.DOCS_NO_DUE_SLIP_HOSTEL,PermissionConstant.DOCS_NO_DUE_SLIP_LIBRARY,PermissionConstant.DOCS_NO_DUE_SLIP_ACCOUNT,PermissionConstant.DOCS_NO_DUE_SLIP_REGISTRAR],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Academic',
                route: '/docs/no-due/academic',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.DOCS_NO_DUE_SLIP_ACADEMIC],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Hostel',
                route: '/docs/no-due/hostel',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.DOCS_NO_DUE_SLIP_HOSTEL],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Library',
                route: '/docs/no-due/library',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.DOCS_NO_DUE_SLIP_LIBRARY],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Account',
                route: '/docs/no-due/account',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.DOCS_NO_DUE_SLIP_ACCOUNT],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Registrar',
                route: '/docs/no-due/registrar',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.DOCS_NO_DUE_SLIP_REGISTRAR],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Other',
                route: '/docs/no-due/other',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.DOCS_NO_DUE_SLIP_OTHER],
                is_hidden: false
              }
            ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Utilities',
        availableTo: [
          PermissionConstant.UTILITIES_HOSTEL,
          PermissionConstant.UTILITIES_HOSTEL_ADD,
          PermissionConstant.UTILITIES_HOSTEL_ADD_ROOM,
          PermissionConstant.UTILITIES_HOSTEL_ADD_MENU,
          PermissionConstant.UTILITIES_HOSTEL_UPDATE,
          PermissionConstant.UTILITIES_HOSTEL_STATUS,
          PermissionConstant.UTILITIES_HOSTEL_DELETE,
          PermissionConstant.UTILITIES_VEHICLE,
          PermissionConstant.UTILITIES_VEHICLE_ADD,
          PermissionConstant.UTILITIES_VEHICLE_ADD_VEHICLE,
          PermissionConstant.UTILITIES_VEHICLE_ADD_ROUTE,
          PermissionConstant.UTILITIES_VEHICLE_UPDATE,
          PermissionConstant.UTILITIES_VEHICLE_STATUS,
          PermissionConstant.UTILITIES_VEHICLE_DELETE,
          PermissionConstant.UTILITIES_HOLIDAY,
          PermissionConstant.UTILITIES_HOLIDAY_ADD,
          PermissionConstant.UTILITIES_HOLIDAY_UPDATE,
          PermissionConstant.UTILITIES_HOLIDAY_DELETE,
          PermissionConstant.UTILITIES_MISCELLANEOUS,
          PermissionConstant.UTILITIES_MISCELLANEOUS_ADD,
          PermissionConstant.UTILITIES_MISCELLANEOUS_UPDATE,
          PermissionConstant.UTILITIES_MISCELLANEOUS_STATUS,
          PermissionConstant.UTILITIES_MISCELLANEOUS_DELETE,
          PermissionConstant.UTILITIES_SCHOLARSHIP,
          PermissionConstant.UTILITIES_SCHOLARSHIP_ADD,
          PermissionConstant.UTILITIES_SCHOLARSHIP_VIEW,
          PermissionConstant.UTILITIES_SCHOLARSHIP_UPDATE,
          PermissionConstant.UTILITIES_SCHOLARSHIP_STATUS,
          PermissionConstant.UTILITIES_SCHOLARSHIP_DELETE,
          PermissionConstant.UTILITIES_REPORT
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Hostels',
            route: '/utilities/hostel',
            icon: 'mat:gite',
            availableTo: [
              PermissionConstant.UTILITIES_HOSTEL,
              PermissionConstant.UTILITIES_HOSTEL_ADD,
              PermissionConstant.UTILITIES_HOSTEL_ADD_ROOM,
              PermissionConstant.UTILITIES_HOSTEL_ADD_MENU,
              PermissionConstant.UTILITIES_HOSTEL_UPDATE,
              PermissionConstant.UTILITIES_HOSTEL_STATUS,
              PermissionConstant.UTILITIES_HOSTEL_DELETE
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Vehicles',
            route: '/utilities/vehicle',
            icon: 'mat:directions_car',
            availableTo: [
              PermissionConstant.UTILITIES_VEHICLE,
              PermissionConstant.UTILITIES_VEHICLE_ADD,
              PermissionConstant.UTILITIES_VEHICLE_ADD_VEHICLE,
              PermissionConstant.UTILITIES_VEHICLE_ADD_ROUTE,
              PermissionConstant.UTILITIES_VEHICLE_UPDATE,
              PermissionConstant.UTILITIES_VEHICLE_STATUS,
              PermissionConstant.UTILITIES_VEHICLE_DELETE
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Holidays',
            route: '/utilities/holiday',
            icon: 'mat:beach_access',
            availableTo: [
              PermissionConstant.UTILITIES_HOLIDAY,
              PermissionConstant.UTILITIES_HOLIDAY_ADD,
              PermissionConstant.UTILITIES_HOLIDAY_UPDATE,
              PermissionConstant.UTILITIES_HOLIDAY_DELETE
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Miscellaneous',
            route: '/utilities/miscellaneous-charge',
            icon: 'mat:payments',
            availableTo: [
              PermissionConstant.UTILITIES_MISCELLANEOUS,
              PermissionConstant.UTILITIES_MISCELLANEOUS_ADD,
              PermissionConstant.UTILITIES_MISCELLANEOUS_UPDATE,
              PermissionConstant.UTILITIES_MISCELLANEOUS_STATUS,
              PermissionConstant.UTILITIES_MISCELLANEOUS_DELETE
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Scholarship',
            route: '/utilities/scholarship',
            icon: 'mat:card_giftcard',
            availableTo: [
              PermissionConstant.UTILITIES_SCHOLARSHIP,
              PermissionConstant.UTILITIES_SCHOLARSHIP_ADD,
              PermissionConstant.UTILITIES_SCHOLARSHIP_VIEW,
              PermissionConstant.UTILITIES_SCHOLARSHIP_UPDATE,
              PermissionConstant.UTILITIES_SCHOLARSHIP_STATUS,
              PermissionConstant.UTILITIES_SCHOLARSHIP_DELETE
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Reports',
            route: '/utilities/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.UTILITIES_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Alumnis',
        availableTo: [PermissionConstant.ALUMNI],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'List',
            route: '/alumni/list',
            icon: 'mat:emoji_people',
            availableTo: [PermissionConstant.ALUMNI_LIST],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Support Tickets',
        availableTo: [PermissionConstant.ALL],
        is_hidden: false,
        children: [
          {
            type: 'dropdown',
            label: 'Student',
            icon: 'mat:confirmation_number',
            availableTo: [PermissionConstant.STUDENT_SUPPORT_TICKET],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Academic',
                route: '/support-ticket/student/academic-ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.STUDENT_SUPPORT_TICKET_ACADEMIC
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Account',
                route: '/support-ticket/student/account-ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.STUDENT_SUPPORT_TICKET_ACCOUNT
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Examination',
                route: '/support-ticket/student/examination-ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.STUDENT_SUPPORT_TICKET_EXAMINATION
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Admission',
                route: '/support-ticket/student/admission-ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.STUDENT_SUPPORT_TICKET_ADMISSION
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Hostel',
                route: '/support-ticket/student/hostel-ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.STUDENT_SUPPORT_TICKET_HOSTEL],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Other',
                route: '/support-ticket/student/other-ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.STUDENT_SUPPORT_TICKET_OTHER],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Employee',
            icon: 'mat:support_agent',
            availableTo: [PermissionConstant.ALL],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Support Ticket Type',
                route: '/support-ticket/employee/support-ticket-type',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.EMPLOYEE_SUPPORT_TICKET_TYPE],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Ticket',
                route: '/support-ticket/employee/ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.ALL],
                is_hidden: !(
                  this.user &&
                  this.user.user_type &&
                  this.user.user_type == 'EMPLOYEE'
                )
              },
              {
                type: 'link',
                label: 'IT Service',
                route: '/support-ticket/employee/it-service-ticket',
                icon: 'mat:school',
                availableTo: [
                  PermissionConstant.EMPLOYEE_SUPPORT_TICKET_IT_SERVICE
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'HR Issue',
                route: '/support-ticket/employee/hr-issue-ticket',
                icon: 'mat:school',
                availableTo: [
                  PermissionConstant.EMPLOYEE_SUPPORT_TICKET_HR_ISSUE
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Vehicle',
                route: '/support-ticket/employee/vehicle-ticket',
                icon: 'mat:school',
                availableTo: [
                  PermissionConstant.EMPLOYEE_SUPPORT_TICKET_VEHICLE
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Administration',
                route: '/support-ticket/employee/administration-ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.EMPLOYEE_SUPPORT_TICKET_ADMINISTRATION
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Academic',
                route: '/support-ticket/employee/academic-ticket',
                icon: 'mat:school',
                availableTo: [
                  PermissionConstant.EMPLOYEE_SUPPORT_TICKET_ACADEMIC
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Other',
                route: '/support-ticket/employee/other-ticket',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.EMPLOYEE_SUPPORT_TICKET_OTHER],
                is_hidden: false
              }
            ]
          },
          {
            type: 'link',
            label: 'Center\'s Ticket',
            route: '/support-ticket/center-support-ticket',
            icon: 'mat:support_agent',
            availableTo: [PermissionConstant.CENTER_SUPPORT_TICKET],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Report',
            route: '/support-ticket/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.SUPPORT_TICKET_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Allegations',
        availableTo: [PermissionConstant.ALLEGATIONS],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Add',
            route: '/allegations/add',
            icon: 'mat:add_circle',
            availableTo: [PermissionConstant.ADD_ALLEGATIONS],
            is_hidden: (this.user && this.user.user_type === 'SUPER_ADMIN')
          },
          {
            type: 'link',
            label: 'List',
            route: '/allegations/list',
            icon: 'mat:view_list',
            availableTo: [PermissionConstant.LIST_ALLEGATIONS],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Transactions',
            route: '/allegations/transactions',
            icon: 'mat:credit_card',
            availableTo: [PermissionConstant.ALLEGATION_TRANSACTIONS],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Tasks',
        availableTo: [PermissionConstant.TASKS],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'List',
            route: '/tasks/list',
            icon: 'mat:add_circle',
            availableTo: [PermissionConstant.TASKS_LIST],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Notifications',
        availableTo: [PermissionConstant.NOTIFICATION],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Student\'s Notification',
            route: '/notification/student-notification',
            icon: 'mat:notifications',
            availableTo: [PermissionConstant.STUDENT_NOTIFICATION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Center\'s Notification',
            route: '/notification/center-notification',
            icon: 'mat:notifications',
            availableTo: [PermissionConstant.CENTER_NOTIFICATION],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Report',
            route: '/notification/report',
            icon: 'mat:dashboard_customize',
            availableTo: [PermissionConstant.NOTIFICATION_REPORT],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Website Landing',
        availableTo: [PermissionConstant.WEBSITE_LANDING],
        is_hidden: false,
        children: [
          {
            type: 'dropdown',
            label: 'Banner',
            icon: 'mat:slideshow',
            availableTo: [PermissionConstant.WEBSITE_LANDING_BANNER],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Top',
                route: '/website-landing/banner/top',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.WEBSITE_LANDING_BANNER_TOP],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Middle',
                route: '/website-landing/banner/middle',
                icon: 'mat:dashboard_customize',
                availableTo: [PermissionConstant.WEBSITE_LANDING_BANNER_MIDDLE],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Promotion',
                route: '/website-landing/banner/promotion',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.WEBSITE_LANDING_BANNER_PROMOTION
                ],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'News & Highlights',
            icon: 'mat:campaign',
            availableTo: [PermissionConstant.WEBSITE_LANDING],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Awards & Achievements',
                route: '/website-landing/news-&-highlights/award-achievement',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.WEBSITE_LANDING_NEWS_HIGHLIGHT_AWARD_ACHIEVEMENT
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Events',
                route: '/website-landing/news-&-highlights/event',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.WEBSITE_LANDING_NEWS_HIGHLIGHT_EVENT
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Announcements',
                route: '/website-landing/news-&-highlights/announcement',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.WEBSITE_LANDING_NEWS_HIGHLIGHT_ANNOUNCEMENT
                ],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Gallery',
            icon: 'mat:photo_library',
            availableTo: [PermissionConstant.WEBSITE_LANDING_GALLERY],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Images / Videos',
                route: '/website-landing/gallery/image-video',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.WEBSITE_LANDING_GALLERY_IMAGE_VIDEO
                ],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Faculties',
            icon: 'mat:manage_accounts',
            availableTo: [PermissionConstant.WEBSITE_LANDING_FACULTIES],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Faculty',
                route: '/website-landing/faculties/faculty',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.WEBSITE_LANDING_FACULTIES_FACULTY
                ],
                is_hidden: false
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Student Voices',
            icon: 'mat:record_voice_over',
            availableTo: [PermissionConstant.WEBSITE_LANDING_STUDENT_VOICES],
            is_hidden: false,
            children: [
              {
                type: 'link',
                label: 'Testimonials',
                route: '/website-landing/student-voice/testimonial',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.WEBSITE_LANDING_STUDENT_VOICES_TESTIMONIAL
                ],
                is_hidden: false
              },
              {
                type: 'link',
                label: 'Success Stories',
                route: '/website-landing/student-voice/success-stories',
                icon: 'mat:dashboard_customize',
                availableTo: [
                  PermissionConstant.WEBSITE_LANDING_STUDENT_VOICES_SUCCESS_STORIES
                ],
                is_hidden: false
              }
            ]
          }
        ]
      }
    ]);
  }

  getSelfDetails(): void {
    this.authService.self().subscribe((data) => {
      if (data && data.id) {
        this.user = data;
        if (this.user && this.user['user_type'] === 'EMPLOYEE') {
          this.checkUserDetails();
        } else {
          if (!sessionStorage.getItem('reloaded')) {
            sessionStorage.setItem('reloaded', 'true');
            location.reload();
          } else {
            sessionStorage.removeItem('reloaded');
            this.loadNavigation();
          }
          // this.visibleAllMenu();
          //this.loadNavigation();
          /*if (!window.location.search.includes('reloaded=true')) {
            window.location.href = window.location.pathname + '?reloaded=true';
          } else {
            this.loadNavigation();
          }*/
        }
      }
    });
  }

  visibleAllMenu(): void {
    this.loadNavigation();
    this._items.forEach((menu) => {
      menu.forEach((item) => {
        item['is_hidden'] = false;
        if (item['children'] && item['children'].length > 0) {
          item['is_hidden'] = false;
          item['children'].forEach((child: any) => {
            child['is_hidden'] = false;
            if (child && child['children'] && child['children'].length > 0) {
              child['is_hidden'] = false;
              item['is_hidden'] = false;
              child['children'].forEach((childrenOfChild: any) => {
                childrenOfChild['is_hidden'] = false;
              });
            }
          });
        }
      });
    });
  }

  checkUserDetails() {
    this.employeeService.detail(this.user?.unique_id).subscribe((data: any) => {
      if (data && data['employee_details'] && data['employee_details']['designation'] && data['employee_details']['designation']['designation_permissions']) {
        this.userData = data['employee_details']['designation']['designation_permissions'];
        let rolePermissions: any = [];
        if (this.userData && this.userData.length > 0) {
          this.userData.forEach((subPermission: any) => {
            if (subPermission && subPermission['sub_permission'] && subPermission['sub_permission']['meta']) {
              rolePermissions.push(subPermission['sub_permission']['meta']);
            }
          });
        }
        this.loadNavigation();
        this._items.forEach((menu) => {
          menu.forEach((item) => {
            item['is_hidden'] = !Helper.permissionCheck(item['availableTo'], rolePermissions);
            if (item['children'] && item['children'].length > 0) {
              item['is_hidden'] = !Helper.childPermissionCheck(item['children'], rolePermissions);

              item['children'].forEach((child: any) => {
                child['is_hidden'] = !Helper.permissionCheck(child['availableTo'], rolePermissions);
                if (child && child['children'] && child['children'].length > 0) {
                  child['is_hidden'] = !Helper.childPermissionCheck(child['children'], rolePermissions);
                  // item['is_hidden'] = !Helper.childPermissionCheck(child['children'], rolePermissions);
                  child['children'].forEach((childrenOfChild: any) => {
                    childrenOfChild['is_hidden'] = !Helper.permissionCheck(childrenOfChild.availableTo, rolePermissions);
                  });
                }
              });
            }
          });
        });
      }
    });
  }
}
